import { OrderCreate, type CustomerSummary, type Item } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { IconMinus, IconPlus, IconX } from '../components/icons';
import { CallAheadBadge, Empty, ErrorBox, Loading, PageHead, QueryView, SearchBox, TierBadge } from '../components/ui';
import { formatMoney, formatQty, isIsoDate, packLabel, parseTyped, todayIso, weekdayName } from '../format';
import { useDebounced } from '../hooks';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import {
  addItem,
  buildOrder,
  canStepDown,
  draftFromLine,
  isoWeekday,
  lineUnit,
  nextDeliveryDay,
  revisedLines,
  lineQty,
  looseUnit,
  orderTotal,
  stepPacks,
  toggleLoose,
  toggleSplit,
  unitPrice,
  type Channel,
  type DraftLine,
  type Tier,
} from '../orders/draft';
import { qk } from '../queryClient';

/** standing and import orders are created by the system, not typed in on a phone. */
const MANUAL_CHANNELS: Channel[] = ['phone', 'whatsapp', 'sms', 'walkin', 'web'];
const PICKER_LIMIT = 50;

export function NewOrderPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // C6: /orders/:id/edit changes a saved order on the same screen
  const { id: editId } = useParams();
  const editing = editId !== undefined;
  const books = me?.role === 'owner' || me?.role === 'office';
  const editingOrder = useQuery({ queryKey: qk.order(editId ?? ''), queryFn: ({ signal }) => api.order(editId ?? '', signal), enabled: editing, staleTime: 0 });
  const presetCustomerId = params.get('customer') ?? (editing ? (editingOrder.data?.customerId ?? null) : null);
  const datePreset = isIsoDate(params.get('date'));

  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [requestedDate, setRequestedDate] = useState(() => {
    const d = params.get('date');
    return isIsoDate(d) ? d : todayIso();
  });
  const [channel, setChannel] = useState<Channel>('phone');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [itemPickerOpen, setItemPickerOpen] = useState(presetCustomerId != null);
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const lineSeq = useRef(0);
  // One Idempotency-Key per attempt: kept while retrying the same payload after a network failure
  // (the first try may have landed), replaced as soon as the payload changes or the server answers.
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const preset = useQuery({
    queryKey: qk.customer(presetCustomerId ?? ''),
    queryFn: ({ signal }) => api.customer(presetCustomerId ?? '', signal),
    enabled: presetCustomerId != null,
  });
  useEffect(() => {
    if (preset.data) setCustomer((cur) => cur ?? preset.data);
  }, [preset.data]);

  const tier: Tier = customer?.priceTier ?? 'standard';
  const unitWord = (u: string | null) => (u === 'LB' ? t('unitLb') : u === 'EA' ? t('unitEa') : u === 'BN' ? t('unitBn') : (u ?? ''));
  // the prices saving would charge (the customer's rules, the tier, a pound out of the box): the server's own (C6)
  const quoteAsk = customer ? { customerId: customer.id, lines: lines.map((l) => ({ itemId: l.item.id, unit: lineUnit(l) as 'CS' })) } : null;
  const quote = useQuery({
    queryKey: ['orders', 'quote', JSON.stringify(quoteAsk)],
    // the answer carries the lines it was asked about, so a price never lands on another line while the next one loads
    queryFn: async ({ signal }) => ({ ask: quoteAsk!.lines, ...(await api.orderQuote(quoteAsk!, signal)) }),
    enabled: quoteAsk !== null && quoteAsk.lines.length > 0,
    placeholderData: keepPreviousData,
  });
  const quoted = (i: number) => {
    const l = lines[i];
    const d = quote.data;
    if (!l || !d) return undefined;
    const j = d.ask.findIndex((a) => a.itemId === l.item.id && a.unit === lineUnit(l));
    return j < 0 ? undefined : d.lines[j];
  };
  /** a saved line whose unit stays keeps its saved price */
  const keepsPrice = (l: DraftLine) => l.lineNo !== undefined && (!l.touched || lineUnit(l) === l.savedUnit);
  /** the price a line is charged: a typed rate, a saved line's own price (kept), else the server's quote */
  const priceAt = (l: DraftLine, i: number): number | null => {
    if (l.rate != null && (l.rateTyped || keepsPrice(l))) return l.rate;
    if (!customer) return unitPrice(l.item, tier);
    return quoted(i)?.unitPrice ?? null;
  };
  const qtyAt = (l: DraftLine) => (l.lineNo !== undefined && !l.touched ? (l.savedQty ?? lineQty(l)) : lineQty(l));
  const centsAt = (l: DraftLine, i: number): number | null => {
    const p = priceAt(l, i);
    return p == null ? null : Math.round(qtyAt(l) * p * 100);
  };
  const total = customer ? lines.reduce((sum, l, i) => sum + (centsAt(l, i) ?? 0), 0) / 100 : orderTotal(lines, tier);
  // the owner's approval goes with the save it was given for: any change to the order asks again
  const [approval, setApproval] = useState<{ approveFloor?: boolean; overrideHold?: boolean }>({});
  useEffect(() => setApproval({}), [lines, requestedDate]);
  const today = todayIso();
  const notDelivery = customer && customer.deliveryDays.length > 0 && !customer.deliveryDays.includes(isoWeekday(requestedDate));
  const pastDate = requestedDate < today && !(editing && requestedDate === editingOrder.data?.requestedDate);

  // the order being changed, laid out once its products are known
  const catalog = useQuery({ queryKey: qk.items(''), queryFn: ({ signal }) => api.items('', signal), enabled: editing, staleTime: 5 * 60_000 });
  // laid out from the order as read now (never a copy cached from a list), with the version it was read at: the save
  // sends that version, so a store's call typed in meanwhile is never written over
  const [laidVersion, setLaidVersion] = useState<string | null>(null);
  useEffect(() => {
    const o = editingOrder.data;
    if (!editing || laidVersion !== null || !o || !o.version || !editingOrder.isFetchedAfterMount || !catalog.data) return;
    const byId = new Map(catalog.data.map((i) => [i.id, i]));
    setLaidVersion(o.version);
    setRequestedDate(o.requestedDate);
    setNotes(o.notes ?? '');
    setLines(o.lines.map((l) => {
      lineSeq.current += 1;
      const qty = l.qtyDelivered ?? l.qty;
      const known = byId.get(l.itemId);
      // a product since archived, or a line shorted to nothing: shown, sent back as it was
      const item = known ?? ({ ...catalog.data![0]!, id: l.itemId, name: l.itemName, packUnit: l.unit as 'CS', prices: {}, sellLoose: false, sellSplit: false, content: null, contentUnit: null } as Item);
      return draftFromLine({ lineNo: l.lineNo, qty, unit: l.unit, unitPrice: l.unitPrice }, item, `line-${lineSeq.current}`, !known || !(qty > 0));
    }));
  }, [editing, editingOrder.data, editingOrder.isFetchedAfterMount, catalog.data, laidVersion]);
  // read again since (the screen came back into view): changed by someone else meanwhile
  const changedMeanwhile = editing && laidVersion !== null && !!editingOrder.data?.version && editingOrder.data.version !== laidVersion;

  const change = useMutation({
    mutationFn: (flags: { approveFloor?: boolean; overrideHold?: boolean }) =>
      api.updateOrder(editId!, { version: laidVersion ?? '', requestedDate, notes: notes.trim() || null, lines: revisedLines(lines), ...flags }),
    onSuccess: (order) => {
      qc.setQueryData(qk.order(order.id), order);
      void qc.invalidateQueries({ queryKey: qk.orderLists });
      void qc.invalidateQueries({ queryKey: ['forecast', 'order-day'] });
      if (mounted.current) navigate(`/orders/${order.id}`, { replace: true, state: { saved: true } });
    },
  });
  /** the owner, told why the server stopped (under the floor or cost, a hold, the credit limit), approves and it goes again */
  const ownerMayApprove = (err: unknown): { approveFloor?: boolean; overrideHold?: boolean } | null => {
    if (me?.role !== 'owner' || !(err instanceof ApiError)) return null;
    const why = `${err.problem.detail ?? ''} ${err.problem.errors?.map((e) => e.message).join(' ') ?? ''}`;
    if (err.status === 422 && /floor|cost/i.test(why)) return { approveFloor: true };
    if (err.status === 409 && /hold|credit limit/i.test(why)) return { overrideHold: true };
    return null;
  };
  const create = useMutation({
    mutationFn: (v: { body: OrderCreate; key: string }) => api.createOrder(v.body, v.key),
    // Safe to repeat: the same key rides along, so the server creates the order at most once.
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: (order) => {
      attempt.current = null;
      qc.setQueryData(qk.order(order.id), order);
      void qc.invalidateQueries({ queryKey: qk.orderLists });
      if (mounted.current) navigate(`/orders/${order.id}`, { replace: true, state: { saved: true } });
    },
    onError: (error) => {
      if (!(error instanceof ApiError && error.status === 0)) attempt.current = null;
    },
  });

  function chooseCustomer(c: CustomerSummary) {
    setCustomer(c);
    setProblem(null);
    // a phone order is for their next delivery day (C6), a walk-in for today, unless the link said a day
    if (!editing && !datePreset) setRequestedDate(channel === 'walkin' ? todayIso() : nextDeliveryDay(todayIso(), c.deliveryDays));
    if (lines.length === 0) setItemPickerOpen(true);
  }

  function pickItem(item: Item) {
    lineSeq.current += 1;
    const key = `line-${lineSeq.current}`;
    setLines((cur) => addItem(cur, item, key));
    setItemPickerOpen(false);
    setProblem(null);
  }

  const updateLine = (key: string, change: (l: DraftLine) => DraftLine) =>
    setLines((cur) => cur.map((l) => (l.key === key ? { ...change(l), touched: true } : l)));

  function save(extra: { approveFloor?: boolean; overrideHold?: boolean } = approval) {
    if (!customer) return setProblem('pickCustomer');
    if (lines.length === 0) return setProblem('addItems');
    if (lines.some((l) => l.loose != null && !(l.loose > 0))) return setProblem('qtyNeeded');
    if (pastDate) return setProblem('orderDatePast');
    if (editing) {
      if (!editingOrder.data) return;
      setProblem(null);
      change.mutate(extra);
      return;
    }
    const parsed = OrderCreate.safeParse({
      ...buildOrder({ customerId: customer.id, requestedDate, channel, notes, lines }),
      ...extra,
    });
    if (!parsed.success) return setProblem('error');
    setProblem(null);
    const serialized = JSON.stringify(parsed.data);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    create.mutate({ body: parsed.data, key: attempt.current.key });
  }

  return (
    <div className="stack">
      {changedMeanwhile ? (
        <p className="warn-band" role="alert" data-testid="order-changed-meanwhile">
          {t('orderChangedMeanwhile')}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLaidVersion(null)}>
            {t('reloadOrder')}
          </button>
        </p>
      ) : null}
      <PageHead title={editing ? `${t('editOrder')}${editingOrder.data ? ` #${editingOrder.data.orderNo}` : ''}` : t('newOrder')} back={editing ? `/orders/${editId}` : '/orders'} />
      {editing && editingOrder.data && !['draft', 'confirmed'].includes(editingOrder.data.status) ? (
        <p className="warn-band" role="note">{t('orderNotEditable')}</p>
      ) : null}

      <section className="stack-sm">
        <h2>{t('customer')}</h2>
        {customer ? (
          <div className="card chosen">
            <div className="row-main">
              <span className="row-title">{customer.name}</span>
              <span className="row-sub">
                <TierBadge tier={customer.priceTier} />
                {customer.callAhead ? <CallAheadBadge /> : null}
              </span>
            </div>
            {editing ? null : (
              <button type="button" className="btn btn-secondary" onClick={() => setCustomer(null)}>
                {t('change')}
              </button>
            )}
          </div>
        ) : presetCustomerId && preset.isPending ? (
          <Loading />
        ) : (
          <CustomerPicker onPick={chooseCustomer} />
        )}
      </section>

      <label className="field">
        <span>{t('deliveryDate')}</span>
        <input
          type="date"
          value={requestedDate}
          min={today}
          onChange={(e) => {
            if (isIsoDate(e.target.value)) setRequestedDate(e.target.value);
          }}
        />
        {pastDate ? <span className="field-error" role="alert">{t('orderDatePast')}</span> : null}
        {notDelivery && !pastDate ? (
          <span className="muted small" data-testid="not-delivery-day">
            {t('notTheirDeliveryDay').replace('{days}', customer.deliveryDays.map((d) => weekdayName(d, intlLocale)).join(', '))}
          </span>
        ) : null}
      </label>

      {editing ? null : (
      <section className="stack-sm">
        <h2>{t('channel')}</h2>
        <div className="chips" role="group" aria-label={t('channel')}>
          {MANUAL_CHANNELS.map((c) => (
            <button
              key={c}
              type="button"
              className="chip"
              aria-pressed={channel === c}
              onClick={() => {
                setChannel(c);
                // a walk-in is sold today; the others go out on the customer's next delivery day
                if (!datePreset && customer && (c === 'walkin') !== (channel === 'walkin')) setRequestedDate(c === 'walkin' ? todayIso() : nextDeliveryDay(todayIso(), customer.deliveryDays));
              }}
            >
              {t(`channel_${c}`)}
            </button>
          ))}
        </div>
      </section>
      )}

      <section className="stack-sm">
        <h2>{t('items')}</h2>
        {lines.length ? (
          <ul className="lines">
            {lines.map((l, i) => {
              const price = priceAt(l, i);
              const cents = centsAt(l, i);
              // a saved line keeps its price: only a new or re-priced line is the owner's to approve
              const needsOwner = quoted(i)?.needsOwner === true && !l.rateTyped && !keepsPrice(l);
              if (l.fixed) {
                return (
                  <li key={l.key} className="card line" data-testid="fixed-line">
                    <div className="row-main">
                      <span className="row-title">{l.item.name}</span>
                      <span className="row-sub">
                        {formatQty(l.savedQty ?? 0)} {unitWord(l.savedUnit ?? null)} · {t('lineKeptAsIs')}
                      </span>
                    </div>
                  </li>
                );
              }
              return (
                <li key={l.key} className="card line">
                  <div className="line-head">
                    <div className="row-main">
                      <span className="row-title">{l.item.name}</span>
                      <span className="row-sub">
                        {packLabel(l.item)} · {price != null ? formatMoney(price, intlLocale) : t('noPrice')}
                        {needsOwner ? <span className="badge badge-danger"> {t('needsOwnerOk')}</span> : null}
                      </span>
                      {/* a line with no price waits for one: the owner or the office types it (C6) */}
                      {books && (price == null || l.rateTyped) ? (
                        <label className="rate-field">
                          <span className="muted small">{t('rate')}</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`${t('rate')}: ${l.item.name}`}
                            defaultValue={l.rateTyped && l.rate != null ? String(l.rate) : ''}
                            onChange={(e) => {
                              const n = parseTyped(e.target.value);
                              updateLine(l.key, (x) => ({ ...x, rate: n !== null && n > 0 ? Math.round(n * 100) / 100 : null, rateTyped: n !== null && n > 0 }));
                            }}
                          />
                        </label>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`${t('remove')}: ${l.item.name}`}
                      onClick={() => setLines((cur) => cur.filter((x) => x.key !== l.key))}
                    >
                      <IconX />
                    </button>
                  </div>
                  <div className="line-controls">
                    {l.loose != null ? (
                      <label className="loose-qty">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`${t('qty')} (${unitWord(looseUnit(l.item))}): ${l.item.name}`}
                          defaultValue={l.loose ? String(l.loose) : ''}
                          placeholder="0"
                          onChange={(e) => {
                            const n = Number(e.target.value.replace(',', '.'));
                            updateLine(l.key, (x) => ({ ...x, loose: Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0 }));
                          }}
                        />{' '}
                        {unitWord(looseUnit(l.item))}
                      </label>
                    ) : (
                    <div className="stepper" role="group" aria-label={`${t('qty')}: ${l.item.name}`}>
                      <button
                        type="button"
                        aria-label={`${t('less')}: ${l.item.name}`}
                        disabled={!canStepDown(l)}
                        onClick={() => updateLine(l.key, (x) => stepPacks(x, -1))}
                      >
                        <IconMinus />
                      </button>
                      <output data-testid={`qty-${l.item.id}`}>{formatQty(lineQty(l))}</output>
                      <button
                        type="button"
                        aria-label={`${t('more')}: ${l.item.name}`}
                        onClick={() => updateLine(l.key, (x) => stepPacks(x, 1))}
                      >
                        <IconPlus />
                      </button>
                    </div>
                    )}
                    {looseUnit(l.item) ? (
                      <button type="button" className="toggle" aria-pressed={l.loose != null} onClick={() => updateLine(l.key, toggleLoose)}>
                        {unitWord(looseUnit(l.item))}
                      </button>
                    ) : null}
                    {l.item.sellSplit && l.loose == null ? (
                      <button
                        type="button"
                        className="toggle"
                        aria-pressed={l.split}
                        onClick={() => updateLine(l.key, toggleSplit)}
                      >
                        ½ {t('split')}
                      </button>
                    ) : null}
                    <span className="line-total">{cents != null ? formatMoney(cents / 100, intlLocale) : '—'}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {itemPickerOpen ? (
          <ItemPicker tier={tier} onPick={pickItem} onCancel={() => setItemPickerOpen(false)} />
        ) : (
          <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => setItemPickerOpen(true)}>
            <IconPlus /> {t('addLine')}
          </button>
        )}
      </section>

      <label className="field">
        <span>{t('notes')}</span>
        <textarea value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
      </label>

      {problem ? (
        <p className="field-error" role="alert">
          {t(problem)}
        </p>
      ) : null}
      {create.isError ? <ErrorBox error={create.error} /> : null}
      {change.isError ? <ErrorBox error={change.error} /> : null}
      {/* the owner, told why the server stopped, approves it here and it goes again */}
      {(() => {
        const err = create.isError ? create.error : change.isError ? change.error : null;
        const f = err ? ownerMayApprove(err) : null;
        return f ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              const next = { ...approval, ...f };
              setApproval(next);
              save(next);
            }}
          >
            {f.approveFloor ? t('approveFloorSave') : t('overrideHoldSave')}
          </button>
        ) : null;
      })()}

      <div className="actionbar">
        <div className="total">
          <span className="muted">{t('total')}</span>
          <strong data-testid="order-total">{formatMoney(total, intlLocale)}</strong>
        </div>
        <button type="button" className="btn btn-primary btn-lg" disabled={create.isPending || change.isPending || (editing && laidVersion === null)} onClick={() => save()}>
          {create.isPending || change.isPending ? t('sending') : t('save')}
        </button>
      </div>
    </div>
  );
}

function CustomerPicker({ onPick }: { onPick: (c: CustomerSummary) => void }) {
  const { t } = useLocale();
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim());
  const list = useQuery({
    queryKey: qk.customers(term),
    queryFn: ({ signal }) => api.customers(term, signal),
    placeholderData: keepPreviousData,
  });
  return (
    <div className="picker">
      <SearchBox value={q} onChange={setQ} />
      <QueryView query={list}>
        {(rows) => {
          const shown = rows.filter((c) => c.active).slice(0, PICKER_LIMIT);
          return shown.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {shown.map((c) => (
                <li key={c.id}>
                  <button type="button" className="row" onClick={() => onPick(c)}>
                    <span className="row-main">
                      <span className="row-title">{c.name}</span>
                      <span className="row-sub">
                        <TierBadge tier={c.priceTier} />
                        {c.callAhead ? <CallAheadBadge /> : null}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}

function ItemPicker({ tier, onPick, onCancel }: { tier: Tier; onPick: (item: Item) => void; onCancel: () => void }) {
  const { t, intlLocale } = useLocale();
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim());
  const list = useQuery({
    queryKey: qk.items(term),
    queryFn: ({ signal }) => api.items(term, signal),
    placeholderData: keepPreviousData,
  });
  return (
    <div className="picker">
      <div className="picker-head">
        <SearchBox value={q} onChange={setQ} autoFocus />
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('cancel')}
        </button>
      </div>
      <QueryView query={list}>
        {(rows) => {
          const shown = rows.filter((i) => i.active).slice(0, PICKER_LIMIT);
          return shown.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {shown.map((item) => {
                const price = unitPrice(item, tier);
                return (
                  <li key={item.id}>
                    <button type="button" className="row" onClick={() => onPick(item)}>
                      <span className="row-main">
                        <span className="row-title">{item.name}</span>
                        <span className="row-sub">{packLabel(item)}</span>
                      </span>
                      <span className="row-end-value">{price != null ? formatMoney(price, intlLocale) : t('noPrice')}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}
